import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@akuna/ui";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@akuna/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useAddExame } from "@/hooks/useHospitalization";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const TIPO_EXAME = ["laboratorial", "imagem", "outro"] as const;
type TipoExame = (typeof TIPO_EXAME)[number];

const STATUS_EXAME = ["pendente", "coletado", "resultado"] as const;
type StatusExame = (typeof STATUS_EXAME)[number];

const schema = z.object({
  tipo: z.enum(TIPO_EXAME),
  descricao: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface Exame {
  id: string;
  solicitado_at: string;
  tipo: string;
  descricao: string;
  resultado?: string | null;
  status: string;
}

interface ExamesTabProps {
  hospitalizationId: string;
  customerId: string;
  exames: Exame[];
}

export function ExamesTab({ hospitalizationId, customerId, exames }: ExamesTabProps) {
  const { t, i18n } = useTranslation("hospitalization");
  const [showForm, setShowForm] = useState(false);
  const addMutation = useAddExame();
  const locale = i18n.language === "pt" ? ptBR : undefined;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { tipo: "laboratorial", descricao: "" },
  });

  async function onSubmit(values: FormValues) {
    try {
      await addMutation.mutateAsync({
        hospitalization_id: hospitalizationId,
        customer_id: customerId,
        tipo: values.tipo,
        descricao: values.descricao,
      });
      toast.success(t("success.exameSaved"));
      form.reset();
      setShowForm(false);
    } catch {
      toast.error(t("errors.saveFailed"));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowForm((v) => !v)}>
          <Plus className="h-4 w-4 mr-1" />
          {t("exames.solicitarExame")}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="pt-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
                <FormField
                  control={form.control}
                  name="tipo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("exames.tipo")}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TIPO_EXAME.map((tp) => (
                            <SelectItem key={tp} value={tp}>
                              {t(`exames.tipoOpcoes.${tp}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="descricao"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("exames.descricao")}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                    {t("exames.cancelar")}
                  </Button>
                  <Button type="submit" disabled={addMutation.isPending}>
                    {t("exames.salvar")}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}

      {exames.length === 0 && !showForm && (
        <p className="text-sm text-muted-foreground text-center py-8">
          {t("exames.semExames")}
        </p>
      )}

      {exames.map((ex) => (
        <Card key={ex.id}>
          <CardContent className="pt-3 pb-3 flex items-start gap-3">
            <div className="flex flex-col gap-1">
              <Badge variant="outline">
                {t(`exames.tipoOpcoes.${ex.tipo as TipoExame}`)}
              </Badge>
              <Badge
                variant={ex.status === "resultado" ? "default" : "secondary"}
                className="text-xs"
              >
                {t(`exames.status.${ex.status as StatusExame}`)}
              </Badge>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{ex.descricao}</p>
              {ex.resultado && (
                <p className="text-sm text-muted-foreground mt-1">{ex.resultado}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {format(new Date(ex.solicitado_at), "dd/MM HH:mm", { locale })}
              </p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
